import React, { PureComponent } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { connect } from 'react-redux';
import { replace } from 'connected-react-router';
import { Switch, Route } from 'react-router-dom';

import Dashboard from './views/dashboard/Dashboard';
import MessageModal from './views/dashboard/modal/message/MessageModal';
import {getParams, roleChanged} from './Utils';
import { windowSizeChanged } from './state/Size';
import { authRequest, inviteRequest } from './Networking';
import {loggedIn, loggedOut, roleChange} from './state/Login';
import Loader from './views/dashboard/components/Loader';
import './App.scss';
import Register from './views/register/Register';
import SetPassword from './views/setpassword/SetPassword';

function getLoginUrl(pathname) {
    let url = 'https://login.olerex.ee';
    if (pathname === '/en' || pathname === '/ru') {
        url += pathname;
    }
    return url;
}

export class App extends PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            data: '',
            nonce: null,
            environmentId: null,
            register: null,
            inRequest: false,
            redirectUrl: '',
            redirectData: '',
            errorModal: null,
        };

        this.form = React.createRef();
        this.inviteForm = React.createRef();
        this.lastInviteHash = null;
    }

    getEncryption = (params = {}) => {
        if (this.state.inRequest) {
            return;
        }
        this.setState({ inRequest: true }, () => {
            const request = {
                "requestType": "getEncryption"
            };
            if (params.invite) {
                request.inviteHash = params.invite;
            }
            this.props.authRequest(request,
                async data => {
                    if (data) {
                        this.setState({ data: data.data, loginUrl: getLoginUrl(this.props.pathname) }, async () => {
                            if (this.form.current) {
                                this.form.current.submit(); //redirect to login.olerex.ee, keep inrequest: true
                            }
                        });
                    } else {
                        await new Promise(x => setTimeout(x, 3000)); //wait before retrying
                        this.setState({ inRequest: false }, () => this.getEncryption(params));
                    }
                }, async error => {
                    await new Promise(x => setTimeout(x, 3000)); //wait before retrying
                    this.setState({ inRequest: false }, () => this.getEncryption(params));
                });
        });
    }

    getLogin = (params, fallback = null) => {
        if (this.state.inRequest) {
            return;
        }
        this.setState({ inRequest: true }, () => {
            this.props.authRequest(
                {
                    "requestType": "validateNonce",
                    "nonce": params.nonce
                },
                async data => {
                    if (data && !data.error) {
                        if (data.register === 1) {
                            this.setState({ inRequest: false, register: data, environmentId: data.environmentId }, () => {
                                this.props.navigateToRegister();
                            });
                        } else {
                            this.setState({ inRequest: false }, () => {
                                const params = new URLSearchParams(window.location.search);
                                params.delete('nonce');
                                params.delete('token'); // don't leave the JWT in the URL/history
                                window.history.replaceState({}, '', window.location.pathname + (params.toString() ? '?' + params : ''));
                                this.props.loggedIn(data, this.state.nonce);
                                if (data.redirectTo) {
                                    window.location.href = data.redirectTo;
                                }
                            });
                        }
                    } else if (fallback) {
                        // Primary credential (JWT) rejected — retry once with the
                        // fallback nonce before giving up, so a stale JWT (old app
                        // build) degrades to the menu nonce instead of logging out.
                        this.setState({ inRequest: false, nonce: fallback }, () => {
                            this.getLogin({ ...params, nonce: fallback }, null);
                        });
                    } else {
                        await new Promise(x => setTimeout(x, 3000)); //wait before retrying
                        this.setState({ inRequest: false }, () => this.getEncryption(params));
                    }
                }, async error => {
                    if (fallback) {
                        this.setState({ inRequest: false, nonce: fallback }, () => {
                            this.getLogin({ ...params, nonce: fallback }, null);
                        });
                    } else {
                        await new Promise(x => setTimeout(x, 3000)); //wait before retrying
                        this.setState({ inRequest: false }, () => this.getEncryption(params));
                    }
                });
        });
    }

    isLoggedIn = (props) => {
        return props.login && props.login.token;
    }

    // A request reported the session token expired mid-use. Try a silent re-auth
    // with the credential we still hold; on success the loggedIn reducer installs a
    // fresh token (and clears the expired flag) and we reload so every open view
    // refetches with it. If we have no credential, or it's itself stale, fall back
    // to the normal logout → login-redirect path.
    handleSessionExpired = () => {
        if (this.state.inRequest) {
            return;
        }
        const credential = this.state.nonce;
        if (!credential) {
            this.props.loggedOut();
            return;
        }
        this.setState({ inRequest: true }, () => {
            this.props.authRequest(
                { "requestType": "validateNonce", "nonce": credential },
                data => {
                    if (data && !data.error) {
                        this.props.loggedIn(data, credential);
                        window.location.reload();
                    } else {
                        this.setState({ inRequest: false }, () => this.props.loggedOut());
                    }
                },
                () => {
                    this.setState({ inRequest: false }, () => this.props.loggedOut());
                });
        });
    };

    resizeEvent = () => {
        this.props.windowSizeChanged(window.innerWidth, window.innerHeight);
    }

    doLogin = () => {
        const params = getParams(this.props.search);
        // Prefer the auth-service JWT (app webview, fresh & short-lived) over the
        // nonce; fall back to the nonce for direct browser logins. validateNonce
        // accepts both.
        const isJwt = params.token && params.token.split('.').length === 3;
        const credential = isJwt ? params.token : params.nonce;
        // When leading with the JWT, keep the menu nonce as a fallback so a stale
        // JWT (e.g. an older app build that doesn't refresh before opening the
        // webview) degrades to the nonce instead of logging the user out. This makes
        // iseteenindus shippable independently of the app version.
        const fallback = (isJwt && params.nonce && params.nonce !== params.token) ? params.nonce : null;
        if (credential) {
            this.setState({ nonce: credential }, () => {
                this.getLogin({ ...params, nonce: credential }, fallback);
            })
        } else {
            this.getEncryption(params);
        }
    }

    componentDidMount() {
        //TODO refactor
        //initial login handling
        const params = getParams(this.props.search);
        const hasFreshCredential = Boolean(params.token || params.nonce);
        if (this.props.pathname === "/register") { //on register path, redirect to root
            this.props.navigateToDashboard();
        } else if (!this.isLoggedIn(this.props) || hasFreshCredential) {
            // (Re)authenticate when not logged in, OR when the URL carries a fresh
            // credential. A cached session restored from localStorage keeps a stale
            // `token` that is otherwise never refreshed (the JWT same-user check
            // skips re-auth), so authenticated requests — business transactions,
            // card limits — break once that token expires. doLogin refreshes it;
            // the active role is preserved in the loggedIn reducer.
            this.doLogin();
        }
        const queryParams = new URLSearchParams(window.location.search);
        const roleId = queryParams.get("roleId");

        if (this.props.login.businessClientData) {
            let roleFound = false;
            this.props.login.businessClientData.forEach((businessClient) => {
                if (businessClient.clientId === roleId) {
                    roleFound = true
                    this.props.roleChange(businessClient);
                }
            })

            if (!roleFound) this.props.roleChange(this.props.login.privateClientData);
        }


        window.addEventListener('resize', this.resizeEvent);
        this.resizeEvent();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        //TODO refactor
        //session expired mid-use — attempt a silent re-auth before anything else
        if (!prevProps.login.sessionExpired && this.props.login.sessionExpired) {
            this.handleSessionExpired();
            return;
        }
        //role change, logout login handling
        if (roleChanged(prevProps, this.props)) {
            if (this.isLoggedIn(this.props)) {
                const queryParams = new URLSearchParams(window.location.search);
                const origin = queryParams.get("origin");
                const isAppOrigin = origin === 'app';

                const currentRole = this.props.login.currentRole;

                if (prevProps.login.currentRole && !isAppOrigin) { //user changing role

                    if (currentRole.clientId) queryParams.set("roleId", currentRole.clientId);
                    else queryParams.delete("roleId");
                    this.props.navigateToDashboard();
                } else {
                    const params = getParams(this.props.search);
                    if ((params.invite || params.nonce) && !isAppOrigin) {
                        //this.props.replaceUrl(this.props.pathname); //clear invite and nonce from url
                    }
                    const inviteRequested = this.requestInviteIfNeeded(params, queryParams);
                    if (!inviteRequested && !isAppOrigin) {
                        this.props.navigateToDashboard();
                    }
                }

                const newSearch = queryParams.toString();
                const newUrl =
                    window.location.pathname + (newSearch ? `?${newSearch}` : "");
                this.props.replaceUrl(newUrl);

            } else {
                this.doLogin();
            }
        } else if (this.props.pathname === "/" && !this.isLoggedIn(this.props) && this.state.register === null && this.state.nonce === null) {
            //path changed from register to root and user not logged in or user navigated back from register, perform login
            this.doLogin();
        } else if (this.isLoggedIn(this.props)) {
            const lang = this.props.login.privateClientData && this.props.login.privateClientData.language;
            if (lang && document.documentElement.getAttribute('lang') !== lang) {
                document.documentElement.setAttribute('lang', lang);
            }
            const params = getParams(this.props.search);
            const queryParams = new URLSearchParams(window.location.search);
            const origin = queryParams.get("origin");
            const isAppOrigin = origin === 'app';

            if (!isAppOrigin) {
                if (params.invite || params.nonce) {
                    //this.props.replaceUrl(this.props.pathname); //clear invite and nonce from url
                }
            }


            if (params.invite) {
                const inviteRequested = this.requestInviteIfNeeded(params, queryParams);
                if (inviteRequested) {
                    const newSearch = queryParams.toString();
                    const newUrl =
                        window.location.pathname + (newSearch ? `?${newSearch}` : "");
                    this.props.replaceUrl(newUrl);
                }
            }
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.resizeEvent);
    }

    onInviteResponse(data) {
        if (data.records.error) {
            this.setState({
                errorModal: {
                    text: data.records.error,
                    kind: 'error',
                },
            });

            return;
        }

        if (data.records.message) {
            this.setState({
                errorModal: {
                    text: data.records.message,
                    kind: 'success',
                },
            });

            return;
        }

        this.setState({
            redirectUrl: data.records.redirectUrl,
            redirectData: data.records.redirectData,
        }, () => {
            if (this.inviteForm.current) {
                this.inviteForm.current.submit();
            }
        });
    }

    requestInviteIfNeeded = (params, queryParams) => {
        if (!params.invite) {
            return false;
        }

        const userId = this.props.login.privateClientData && this.props.login.privateClientData.userId;
        if (!userId) {
            return false;
        }

        if (this.lastInviteHash === params.invite) {
            return false;
        }

        this.lastInviteHash = params.invite;
        this.props.inviteRequest(
            {
                "userId": userId,
                "invite": params.invite,
                "token": this.props.login.token
            },
            async data => {
                if (data) {
                    this.onInviteResponse(data);
                }
            }, async error => {
            });

        if (queryParams) {
            queryParams.delete("invite");
        }

        return true;
    }

    onModalClose = () => {
        this.setState({ errorModal: null });
    }

    render() {
        return (
            <div className="App">
                <Switch>
                    <Route path="/register" render={() => <Register {...{ ...this.state.register, nonce: this.state.nonce, environmentId: this.state.environmentId, onClose: this.props.navigateToDashboard }} />} />
                    {this.isLoggedIn(this.props) && this.props.login.mustChangePassword && <Route path="/" component={SetPassword} />}
                    {this.isLoggedIn(this.props) && !this.props.login.mustChangePassword && <Route path="/" component={Dashboard} />}
                    {!this.isLoggedIn(this.props) && <Route path="/" render={() => <div className="login-redirecting">
                        <form className="login-redirecting-hidden-form" ref={this.form} method="POST" action={this.state.loginUrl}>
                            <input name="data" type="text" value={this.state.data} onChange={(e) => this.setState({ data: e.target.value })} />
                        </form>
                        <Loader className="login-redirecting-loader" />
                    </div>} />}
                </Switch>

                <TransitionGroup>
                    {this.state.errorModal && (
                        <CSSTransition key={"error-modal"} timeout={{enter: 600, exit: 600}} classNames={"modal"}>
                            <div className="modal-translucent" onClick={this.onModalClose}>
                                <MessageModal {...{
                                    ...this.state.errorModal,
                                    title: this.props.familyInviteTranslation[this.state.errorModal.kind === 'error' ? 'errorTitle' : 'successTitle'],
                                    onModalClose: this.onModalClose
                                }} />
                            </div>
                        </CSSTransition>
                    )}
                </TransitionGroup>

                <form className="login-redirecting-hidden-invite-form" ref={this.inviteForm} method="POST" action={this.state.redirectUrl || "https://family.olerex.ee"}>
                    <input name="data" type="text" value={this.state.redirectData} onChange={(e) => this.setState({ data: e.target.value })} />
                </form>
            </div>
        );
    }
}

export default connect(
    state => ({
        pathname: state.router.location.pathname,
        login: state.login,
        search: state.router.location.search,
        familyInviteTranslation: state.translation.familyInvite,
    }),
    (dispatch, ownProps) => ({
        navigateToDashboard: () => dispatch(replace(`/${ownProps.location?.search || ''}`)),
        navigateToRegister: () => dispatch(replace(`/register${ownProps.location?.search || ''}`)),
        replaceUrl: (url) => dispatch(replace(url)),
        windowSizeChanged: (width, height) => dispatch(windowSizeChanged(width, height)),
        authRequest: (body, success, error) => authRequest(dispatch, body, success, error),
        loggedIn: (data, nonce) => dispatch(loggedIn(data, nonce)),
        inviteRequest: (body, success, error) => inviteRequest(dispatch, body, success, error),
        roleChange: (newRole) => dispatch(roleChange(newRole)),
        loggedOut: () => dispatch(loggedOut()),
    }))(App);
